import React from "react";
import { Check, CheckCircle2, Sparkles, ArrowRight, Clock, Loader2 } from "lucide-react";
import { SX } from "../studioTokens";
import {
  canStartCheckout, isManualPlan, planEnded, transitionKind, planFeatures, annualSavingPct, canonicalPlanKey,
  statusMeta, PLAN_PROFESSIONAL, PLAN_STARTER, fmtNum, fmtDate, INTERVAL_SUFFIX,
} from "./billingConst";

const Ribbon = ({ tone, icon: Icon, children }) => (
  <span
    className={`absolute -top-[11px] left-1/2 -translate-x-1/2 inline-flex items-center gap-1 px-3 py-1 rounded-full text-[10.5px] font-semibold whitespace-nowrap ${
      tone === "ok" ? "bg-[#2F7D44] text-white" : "bg-[#A04A97] text-white"
    }`}
  >
    {Icon && <Icon size={11} />}
    {children}
  </span>
);

/* One card per paid plan. The button is the whole point of this component:
   it must offer exactly the transitions validate_transition() accepts, so
   anything outside the table renders as an explanatory note instead of a
   button the backend would reject with billing_validation. */
const PlanCard = ({ plan, balance, monthlyTwin, busy, onCheckout, onChange }) => {
  const manual = isManualPlan(balance);
  const ended = planEnded(balance);
  const samePlan = plan.key === balance.plan_key;
  // An ended plan, or a hand-given one whose period ran out, can be bought again.
  const isCurrent = samePlan && !ended && !(manual && balance.status !== "active");
  const isPending = plan.key === balance.pending_plan_key && !isCurrent;
  const popular = canonicalPlanKey(plan.key) === PLAN_PROFESSIONAL;
  const annual = plan.billing_interval === "annual";
  const saving = annual ? annualSavingPct(plan, monthlyTwin) : null;

  const kind = balance.status === "active" ? transitionKind(balance.plan_key, plan.key) : null;
  // A hand-given plan that hasn't ended moves through Checkout, but only the ways validate_transition() allows.
  const canBuy = canStartCheckout(balance) && !(manual && !ended && !samePlan && !transitionKind(balance.plan_key, plan.key));
  const pending = busy === `plan:${plan.key}`;

  let action = null;
  if (isCurrent) {
    action = (
      <button className={`${SX.btn} ${SX.btnSoft} w-full justify-center`} disabled>
        <CheckCircle2 size={14} /> Current plan
      </button>
    );
  } else if (isPending) {
    action = (
      <button className={`${SX.btn} ${SX.btnSoft} w-full justify-center`} disabled>
        <Clock size={14} /> Scheduled for {fmtDate(balance.period_end)}
      </button>
    );
  } else if (canBuy) {
    action = (
      <button
        className={`${SX.btn} ${popular ? SX.btnPrimary : SX.btnSoft} w-full justify-center`}
        disabled={!!busy}
        onClick={() => onCheckout(plan)}
      >
        {pending ? <Loader2 size={14} className="animate-spin" /> : null}
        Choose {plan.name} <ArrowRight size={14} />
      </button>
    );
  } else if (kind) {
    const label = kind === "scheduled"
      ? "Switch at period end"
      : annual ? "Switch to annual" : "Upgrade now";
    action = (
      <button
        className={`${SX.btn} ${kind === "immediate" ? SX.btnPrimary : SX.btnSoft} w-full justify-center`}
        disabled={!!busy}
        onClick={() => onChange(plan, kind)}
      >
        {pending ? <Loader2 size={14} className="animate-spin" /> : null}
        {label} <ArrowRight size={14} />
      </button>
    );
  } else {
    // Reached only when the backend's own rules forbid the pair. Name the
    // actual reason and the account state, so this reads as a rule rather
    // than as something broken.
    action = (
      <div
        className="text-[12px] text-[#8A8A90] text-center leading-[1.45] px-2 py-2"
        title={`plan_key: ${balance.plan_key} · status: ${balance.status || "unset"}`}
      >
        {balance.status === "active" || manual
          ? "Professional and Annual can't switch to each other directly — move to Starter first."
          : `Your account is ${statusMeta(balance.status).label.toLowerCase()} — settle that before changing plans.`}
      </div>
    );
  }

  return (
    <div
      className={`relative flex flex-col bg-white rounded-[14px] p-6 transition-all duration-150 ${
        popular
          ? "border border-[#A04A97] shadow-[0_0_0_3px_rgba(160,74,151,0.12)]"
          : `border border-[#ECE9EE] ${SX.cardHover}`
      }`}
    >
      {popular && <Ribbon icon={Sparkles}>Most popular</Ribbon>}
      {!popular && saving && <Ribbon tone="ok">Save {saving}%</Ribbon>}

      <div className="text-[16px] font-semibold text-[#1A1A1A]">{plan.name}</div>
      <div className="text-[12.5px] text-[#52525B] mt-1 min-h-[34px] leading-[1.4]">{plan.description}</div>

      <div className="flex items-baseline gap-1 mt-4">
        <span className="text-[34px] font-semibold tracking-[-0.03em] leading-none text-[#1A1A1A]">
          ${fmtNum(plan.price_usd)}
        </span>
        <span className="text-[13px] font-medium text-[#8A8A90]">{INTERVAL_SUFFIX[plan.billing_interval]}</span>
      </div>
      <div className="text-[12px] text-[#8A8A90] mt-1 min-h-[18px]">
        {annual
          ? `≈ $${Math.round(plan.price_usd / 12)}/mo billed once a year`
          : `${fmtNum(plan.credits)} credits every month`}
      </div>

      <ul className="flex flex-col gap-2.5 mt-5 pt-5 border-t border-[#ECE9EE]">
        {planFeatures(plan).map((f) => (
          <li key={f} className="flex items-start gap-2.5 text-[12.5px] text-[#1A1A1A] leading-[1.4]">
            <Check size={14} className="text-[#2F7D44] flex-shrink-0 mt-[2px]" />
            {f}
          </li>
        ))}
      </ul>

      <div className="flex-1" />
      <div className="mt-5">{action}</div>
    </div>
  );
};

const PlanCards = ({ plans, balance, busy, onCheckout, onChange }) => {
  const monthlyTwin = plans.find((p) => canonicalPlanKey(p.key) === PLAN_STARTER);
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-stretch">
      {plans.map((plan) => (
        <PlanCard
          key={plan.key}
          plan={plan}
          balance={balance}
          monthlyTwin={monthlyTwin}
          busy={busy}
          onCheckout={onCheckout}
          onChange={onChange}
        />
      ))}
    </div>
  );
};

export default PlanCards;
